import * as React from 'react';
import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Users,
  CreditCard,
  Activity,
  Settings,
  ChevronLeft,
  Shield,
  HeartPulse,
  BarChart3,
  UserCheck,
  AlertCircle,
  TrendingUp,
} from 'lucide-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useAdminPermissions } from '@/hooks/usePermissions';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/shared/hooks/useToast';

interface NavItem {
  title: string;
  href: string;
  icon: React.ElementType;
}

const adminNavItems: NavItem[] = [
  { title: 'Overview', href: '/admin', icon: Activity },
  { title: 'Revenue Control', href: '/admin/revenue-control-center', icon: TrendingUp },
  { title: 'System Health', href: '/admin/health', icon: HeartPulse },
  { title: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
  { title: 'Governance', href: '/governance?repo=forgenetwork', icon: Shield },
  { title: 'Users', href: '/admin/users', icon: Users },
  { title: 'Affiliates', href: '/admin/affiliates', icon: UserCheck },
  { title: 'Subscriptions', href: '/admin/subscriptions', icon: CreditCard },
  { title: 'Activity', href: '/admin/activity', icon: Activity },
  { title: 'System', href: '/admin/system', icon: Settings },
];

interface AdminLayoutProps {
  children?: React.ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const { canAccessAdmin, isLoading: checkingAdmin } = useAdminPermissions();

  // Redirect if not authenticated or not admin
  React.useEffect(() => {
    if (!checkingAdmin) {
      if (!isAuthenticated || !user) {
        navigate({ to: '/login' });
        return;
      }
      if (!canAccessAdmin) {
        toast({
          title: 'Access Denied',
          description: 'You do not have permission to access the admin panel.',
          variant: 'destructive',
        });
        navigate({ to: '/dashboard' });
      }
    }
  }, [isAuthenticated, user, canAccessAdmin, checkingAdmin, navigate, toast]);

  // Show loading state while checking admin status
  if (checkingAdmin || !canAccessAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {checkingAdmin ? (
                <>
                  <Activity className="h-5 w-5 animate-spin" />
                  Checking permissions...
                </>
              ) : (
                <>
                  <AlertCircle className="h-5 w-5 text-destructive" />
                  Access Denied
                </>
              )}
            </CardTitle>
            <CardDescription>
              {checkingAdmin
                ? 'Verifying your admin access'
                : 'You do not have permission to access this area'}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const getBreadcrumbs = () => {
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);

    return segments.map((segment, index) => {
      const href = '/' + segments.slice(0, index + 1).join('/');
      const title = segment.charAt(0).toUpperCase() + segment.slice(1);
      return { href, title };
    });
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <div className="min-h-screen bg-background">
      {/* Admin Header */}
      <header className="sticky top-0 z-30 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-16 items-center px-6">
          <div className="flex items-center gap-4 flex-1">
            {/* Back to main app */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate({ to: '/dashboard' })}
              className="gap-2"
            >
              <ChevronLeft className="h-4 w-4" />
              Back to App
            </Button>

            {/* Breadcrumbs */}
            <nav className="flex items-center gap-2 text-sm">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.href}>
                  {index > 0 && <span className="text-muted-foreground">/</span>}
                  <Link
                    to={crumb.href}
                    className={cn(
                      'hover:text-foreground transition-colors',
                      index === breadcrumbs.length - 1
                        ? 'text-foreground font-medium'
                        : 'text-muted-foreground'
                    )}
                  >
                    {crumb.title}
                  </Link>
                </React.Fragment>
              ))}
            </nav>
          </div>

          {/* Admin Badge */}
          <div className="flex items-center gap-2 text-sm">
            <Shield className="h-4 w-4 text-primary" />
            <span className="font-medium">Admin Panel</span>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Admin Sidebar */}
        <aside className="w-64 border-r bg-card min-h-[calc(100vh-4rem)]">
          <nav className="space-y-1 p-4">
            {adminNavItems.map((item) => {
              const isActive =
                item.href === '/admin'
                  ? location.pathname === '/admin'
                  : location.pathname.startsWith(item.href);
              const Icon = item.icon;

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  <span>{item.title}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}
